// ════════════════════════════════════════════════════════════════════
// src/services/whatsapp.js — a única porta de saída da tela WhatsApp
// ════════════════════════════════════════════════════════════════════
// Tudo vai para o workflow "FaLei Portal · WhatsApp API" no n8n, sempre
// com o token do login. É lá que se confere de qual conta você é e
// quais números são seus — esconder botão aqui seria só enfeite.
//
// Nenhuma chave neste arquivo, de propósito: quem fala com o Supabase e
// com a Evolution é o n8n.
// ════════════════════════════════════════════════════════════════════

import { auth } from './firebase';

const WEBHOOK = 'https://fatima.agenciafalei.com.br/webhook/portal-whatsapp';

async function chamar(acao, dados = {}) {
  const u = auth.currentUser;
  if (!u) throw new Error('Sessão expirada. Entre de novo.');

  const r = await fetch(WEBHOOK, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ acao, token: await u.getIdToken(), ...dados }),
  });

  // Lê como texto primeiro: resposta quebrada é FALHA, não "lista vazia".
  // Senão a tela diria, calma, que não chegou nenhuma conversa.
  const cru = await r.text();
  if (!r.ok) {
    throw new Error(`Falha na comunicação com o servidor (${r.status}). ${cru.slice(0, 140)}`.trim());
  }
  let corpo;
  try {
    corpo = JSON.parse(cru);
  } catch {
    throw new Error(
      `O servidor respondeu ${r.status} mas o conteúdo veio quebrado` +
      (cru ? `: ${cru.slice(0, 140)}` : ' (resposta vazia)') + '.'
    );
  }
  if (corpo && corpo.ok === false) throw new Error(corpo.motivo || 'Não foi possível concluir.');
  if (!corpo || typeof corpo !== 'object') throw new Error('O servidor respondeu algo que eu não sei ler.');
  return corpo;
}

// conferirGravacao: só quando a tela abre (custa uma chamada à Evolution
// por número). incluirSemResposta: traz também os disparos que ninguém
// respondeu.
export const listarConversas = (opcoes = {}) => chamar('conversas', opcoes);
export const lerMensagens = (instancia, contato) => chamar('mensagens', { instancia, contato });
export const responder = (instancia, contato, texto) => chamar('enviar', { instancia, contato, texto });
export const lerMidia = (instancia, msgId) => chamar('midia', { instancia, msgId });

// `undefined` = não mexe; donoUid `null` = tira o dono.
export const marcar = (instancia, contato, mudancas) => chamar('marcar', { instancia, contato, ...mudancas });

// Só o administrador da conta.
export const salvarEtiqueta = (dados) => chamar('salvarEtiqueta', dados);
export const apagarEtiqueta = (id) => chamar('apagarEtiqueta', { id });
export const tirarPessoa = (uid) => chamar('tirarPessoa', { uid });

// Áudio gravado, imagem, vídeo, arquivo. base64 SEM o prefixo `data:`.
// comoVoz: o balãozinho de áudio gravado (não um arquivo de música).
export const enviarMidia = (instancia, contato, dados) => chamar('enviarMidia', { instancia, contato, ...dados });

// Respostas prontas ("/" na caixa). As três devolvem a lista atualizada.
export const lerAtalhos = () => chamar('atalhos').then((r) => r.atalhos || []);
export const salvarAtalho = (dados) => chamar('salvarAtalho', dados).then((r) => r.atalhos || []);
export const apagarAtalho = (id) => chamar('apagarAtalho', { id }).then((r) => r.atalhos || []);

// Editar mensagem de texto enviada (o WhatsApp só deixa até 15 min depois).
export const editarMensagem = (instancia, contato, msgId, texto) =>
  chamar('editarMensagem', { instancia, contato, msgId, texto });

// Nome e observação do contato (nome vazio volta a usar o do WhatsApp).
export const editarContato = (instancia, contato, dados) => chamar('editarContato', { instancia, contato, ...dados });

// Nota interna: fica na conversa só para a equipe; o cliente não recebe.
export const notaInterna = (instancia, contato, texto) => chamar('notaInterna', { instancia, contato, texto });

// Atendimento: lida (vale para a equipe toda), fechar com motivo, reabrir,
// e as fechadas (só quando alguém abre a aba "Fechadas").
export const marcarLida = (instancia, contato) => chamar('marcarLida', { instancia, contato });
export const fecharAtendimento = (instancia, contato, motivo, motivoContato) =>
  chamar('fecharAtendimento', { instancia, contato, motivo, motivoContato });
export const reabrirAtendimento = (instancia, contato) => chamar('reabrirAtendimento', { instancia, contato });
export const listarFechadas = () => chamar('fechadas').then((r) => r.conversas || []);

// Indicadores (botão 📊): só números, calculados no servidor.
// periodo: hoje | 7d | 30d | mes · atendente: uid, ou null para geral.
export const lerIndicadores = (periodo, atendente) => chamar('indicadores', { periodo, atendente });

// Motivos do contato da conta: { motivos: [{ id, nome }], podeEditar }.
// id vazio = lista padrão (a conta ainda não editou); por isso renomear
// e apagar mandam também o nome.
export const lerMotivos = () => chamar('motivos');
export const salvarMotivo = ({ id, nomeAntigo, nome }) => chamar('salvarMotivo', { id: id || null, nomeAntigo: nomeAntigo || null, nome });
export const apagarMotivo = ({ id, nome }) => chamar('apagarMotivo', { id: id || null, nome });

// ── Carteira (aba Clientes) ─────────────────────────────────────────
// Mora no Firebase, num documento só (users/{conta}/clientes/dados) —
// é lida uma vez quando a tela abre, sem passar pelo Supabase. Serve
// para mostrar "Cliente: Fulano" quando o telefone bate, e para o
// vínculo manual no editar contato.
export async function lerCarteira(contaUid) {
  const u = auth.currentUser;
  if (!u || !contaUid) return [];
  const r = await fetch(
    `https://firestore.googleapis.com/v1/projects/migravip-e1afb/databases/(default)/documents/users/${contaUid}/clientes/dados`,
    { headers: { Authorization: `Bearer ${await u.getIdToken()}` } });
  if (r.status === 404) return [];
  if (!r.ok) throw new Error(`Não consegui ler a carteira de clientes (${r.status}).`);
  const doc = await r.json();
  try {
    return JSON.parse(doc.fields?.data?.stringValue || '[]')
      .filter((c) => c && c.id)
      .map((c) => ({
        id: String(c.id),
        codigo: String(c.clienteId || '').trim(),           // o "Cliente ID" da aba Clientes
        nome: c.nome || '(sem nome)',
        documento: String(c.documento || '').replace(/\D/g, ''),
        telefone: String(c.telefone || '').replace(/\D/g, ''),
      }));
  } catch (e) {
    return [];
  }
}
